import {defineStore} from "pinia";
import { ref } from 'vue'
import {useUserRole} from "@/utils/useUserRole";
import moment from "moment-timezone";
import {useBooking} from "@/store/useBooking";
import {useCalendarStore} from "@/store/useCalendarStore";
import {useLocationStore} from "@/utils/LocationStore";
import {useUserStore} from "@/utils/UserStore";
import {useGlobalSetupStore} from "@/utils/GlobalSetupStore";
import {pb} from "@/utils/PocketBaseAdapter";
import {getBookingStatus} from "@/utils/Formatting";
import { useRoute, useRouter } from "vue-router";

export const bookingGlobalPopupStore = defineStore("bookingGlobalPopupStore", () => {
  // Interface
  const isBookingStepperVisible = ref(false)

  const isEditing = ref(false)
  const route = useRoute()

  const router = useRouter()
  const calendar = useCalendarStore();
  const booking = useBooking();
  const location = useLocationStore();
  const userStore = useUserStore();
  const userRole = useUserRole();
  const globalStore = useGlobalSetupStore();
  const bookingLoading = ref(false)

  const { getAllAreas, getAreaLists, getActivityList } = booking;
  const { globalSetup } = storeToRefs(globalStore);
  const { currentLocation, currentLocationDetails } = storeToRefs(userStore);
  const { rolesList, userInfo, permissionListMap } = storeToRefs(userRole);
  const { deleteBooking, getUserList } = calendar;
  const { locationList } = storeToRefs(location);
  const {
    editBookingData,
    areaLists,
    listArea,
    activityList,
    selectedActivity,
    timeRanges,
    membershipStartHour,
    membershipFinishHour,
  } = storeToRefs(booking);

  const getMyRole = computed(() => {
    return rolesList?.value?.filter((role: any) =>
      userInfo.value?.roles?.includes(role.id)
    );
  });

  const {
    usersList,
    recurringDetails,
  } = storeToRefs(calendar);

  const closeDialog = () => {
    isBookingStepperVisible.value = false
    if(route.name == "pos" && isEditing.value){
      isEditing.value = false
      window.location = window.location
    } else {
      isEditing.value = false
    }
  }

  const userId = ref<any>(pb?.authStore?.model?.id);

  const handleRefetchingData = async (bookingId: string) => {
    try {
      // Fetch the booking record from the database, expanding the 'docketLine' relation.
      const bookingData = await pb.collection("bookings").getOne(bookingId, {
        expand: "docketLine",
      });

      const status = getBookingStatus(
        bookingData?.actualStartTime,
        bookingData?.actualDuration
      );

      editBookingData.value = {
        ...bookingData,
        status,
        startDate: bookingData?.startTime,
        docketId: bookingData?.expand?.docketLine?.docket,
      };
    } catch (e) {
      // Log any errors that occur during the fetch operation.
    }
  };

  const canView = (bookingData:any) => {
    if(permissionListMap.value && permissionListMap.value.bookings && Array.isArray(permissionListMap.value.bookings) && permissionListMap.value.bookings.includes("Create")){
      return true
    }
    if(bookingData.owner == userId.value){
      return true;
    }
    if(bookingData.source === 'Guest Booking'){
      return true
    }

    return false
  }

  const { getUserRolePermission } = userRole;


  const quickMakeBooking = async () => {
    bookingLoading.value = true
    await getUserRolePermission()

    let bookingData: any = {"startDate": new Date().toISOString(), "owner": pb.authStore?.model?.id}

    const status = getBookingStatus(
      bookingData?.actualStartTime,
      bookingData?.actualDuration
    );

    editBookingData.value = {
      ...bookingData,
      status,
      startDate: bookingData?.startTime,
      docketId: bookingData?.expand?.docketLine?.docket,
    };

    editBookingData.value.selectedOptions = bookingData.options;

    await getAllAreas();

    // Fetch the area lists based on the selected activity, visible date, and user's membership
    await getAreaLists(
      bookingData.activity,
      bookingData.startTime
        ? moment(bookingData.startTime).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      userInfo?.value?.membership
    );

    window.setTimeout(async () => {
      await getActivityList(userInfo?.value?.membership, bookingData.startTime);
      isBookingStepperVisible.value = true
      bookingLoading.value = false
    }, 500)

  }

  const openPopup = async(bookingId:any) => {
    try{
      isBookingStepperVisible.value = false
      if (bookingId) {
        bookingLoading.value = true
        await getUserRolePermission()
        // Get Booking Details
        let bookingData = await pb.collection("bookings").getOne(bookingId, {expand: "docketLine"})

        if(bookingData && bookingData.startTime && canView(bookingData)) {
          const status = getBookingStatus(
            bookingData?.actualStartTime,
            bookingData?.actualDuration
          );

          editBookingData.value = {
            ...bookingData,
            status,
            startDate: bookingData?.startTime,
            docketId: bookingData?.expand?.docketLine?.docket,
          };

          editBookingData.value.selectedOptions = bookingData.options;

          await getAllAreas();

          // Fetch the area lists based on the selected activity, visible date, and user's membership
          await getAreaLists(
            bookingData.activity,
            bookingData.startTime
              ? moment(bookingData.startTime).format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD"),
            userInfo?.value?.membership
          );

          window.setTimeout(async() => {
            await getActivityList(userInfo?.value?.membership, bookingData.startTime);
            isBookingStepperVisible.value = true
            bookingLoading.value = false
          }, 500)
        } else if(bookingData && bookingData.startTime && !canView(bookingData) && !pb.authStore?.model?.id){
          let to = {
            name: route.name,
            path:route.path,
            query:route.query,
            params:route.params
          }
          try {
            let uowt = JSON.stringify(to)
            sessionStorage.setItem("userOnWayTo", uowt);
            await router.push("/login")
          } catch(e){

          }
        }
      }
    } catch(e){
      bookingLoading.value = false
    }
  }

  watch(route, (to) => {
    let params = new URLSearchParams(document.location.search);
    let bookingId = params.get("booking")
    openPopup(bookingId)

  }, {immediate: true, deep: true})

  return {
    isBookingStepperVisible,
    editBookingData,
    recurringDetails,
    activityList,
    listArea,
    usersList,
    locationList,
    currentLocation,
    getMyRole,
    userInfo,
    userId,
    permissionListMap,
    globalSetup,
    bookingLoading,
    isEditing,
    openPopup,
    getUserList,
    closeDialog,
    deleteBooking,
    handleRefetchingData,
    quickMakeBooking,
  }
})
